import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Wallet, ArrowDownToLine, AlertCircle, ArrowLeft, Calendar } from "lucide-react";
import { api } from "@/lib/api";

const WD_BADGE = {
  paid:      "bg-teal-100 text-teal-800 border-teal-200",
  processed: "bg-teal-100 text-teal-800 border-teal-200",
  submitted: "bg-blue-100 text-blue-700 border-blue-200",
  pending:   "bg-amber-100 text-amber-800 border-amber-200",
  creating:  "bg-amber-100 text-amber-800 border-amber-200",
  failed:    "bg-red-100 text-red-700 border-red-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function CleanerEarnings() {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wdOpen, setWdOpen] = useState(false);
  const [wdAmount, setWdAmount] = useState("");

  const load = async () => {
    try {
      const p = await api.get("/cleaner/profile"); setProfile(p.data);
      if (p.data?.verification_status === "approved") {
        const e = await api.get("/cleaner/earnings"); setEarnings(e.data);
      }
    } catch (_) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const doWithdraw = async () => {
    const amt = parseFloat(wdAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      const { data } = await api.post("/cleaner/withdraw", { amount: amt });
      toast.success(`Withdrawn — new balance $${data.new_balance.toFixed(2)}`);
      setWdOpen(false); setWdAmount(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Withdrawal failed"); }
  };

  if (loading || !profile) return <div className="max-w-3xl mx-auto p-12 text-slate-500" data-testid="earnings-loading">Loading…</div>;

  if (profile.verification_status !== "approved") {
    return (
      <div className="max-w-3xl mx-auto p-12 text-center" data-testid="earnings-not-approved">
        <AlertCircle className="w-14 h-14 text-amber-500 mx-auto" />
        <h2 className="font-display font-bold text-2xl mt-4">Awaiting verification</h2>
        <p className="text-slate-600 mt-2">Earnings become available once your identity is approved.</p>
        <Button className="mt-6 bg-teal-600 text-white" onClick={() => nav("/cleaner")}>Back to portal</Button>
      </div>
    );
  }

  const withdrawals = earnings?.withdrawals || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6">
        <button onClick={() => nav("/cleaner")} className="text-xs font-mono uppercase tracking-widest text-teal-600 hover:text-teal-700 inline-flex items-center gap-1" data-testid="back-to-portal">
          <ArrowLeft className="w-3 h-3" />Back to Portal
        </button>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 mt-2">Earnings</h1>
        <p className="text-sm text-slate-500 mt-1">Withdrawable balance, payout status, and payout history.</p>
      </div>

      {earnings && !earnings.payouts_enabled && (
        <Card className="mb-4 border-amber-300 bg-amber-50" data-testid="payouts-setup-card">
          <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-3 justify-between">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-900">Set up payouts to withdraw</div>
                <div className="text-sm text-amber-700">Connect your bank securely through Stripe. CLEANBAZ never sees or stores your account numbers.</div>
                {earnings.stripe_status?.currently_due?.length > 0 && (
                  <div className="text-xs text-amber-600 mt-1 font-mono">Stripe requires: {earnings.stripe_status.currently_due.join(", ")}</div>
                )}
              </div>
            </div>
            <Button
              onClick={async () => {
                try {
                  const { data } = await api.post("/connect/onboarding-link", { origin: window.location.origin });
                  window.location.href = data.url;
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Could not start payout setup");
                }
              }}
              data-testid="setup-payouts-btn"
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              Set up payouts
            </Button>
          </CardContent>
        </Card>
      )}

      {earnings && (
        <Card className="mb-8 border-teal-200 bg-gradient-to-r from-teal-50 via-white to-white" data-testid="earnings-card">
          <CardContent className="p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-teal-600 flex items-center justify-center teal-glow">
                <Wallet className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-teal-600">Withdrawable balance</div>
                <div className="font-display font-extrabold text-3xl text-slate-900" data-testid="earnings-balance">${earnings.available_balance.toFixed(2)}</div>
                <div className="text-xs text-slate-500 mt-1">
                  Pending ${(earnings.pending_earnings || 0).toFixed(2)} · Earned ${earnings.total_earned.toFixed(2)} · Withdrawn ${earnings.total_withdrawn.toFixed(2)}
                </div>
                {earnings.stripe_status?.external_last4 && (
                  <div className="text-[11px] text-slate-500 mt-0.5 font-mono">Payout to {earnings.stripe_status.external_bank_name || "bank"} ••••{earnings.stripe_status.external_last4}</div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => { setWdAmount(earnings.available_balance.toFixed(2)); setWdOpen(true); }}
                disabled={earnings.available_balance <= 0 || !earnings.payouts_enabled}
                data-testid="withdraw-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white"
                title={!earnings.payouts_enabled ? "Complete Stripe payout setup first" : ""}
              >
                <ArrowDownToLine className="w-4 h-4 mr-1" />Withdraw
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <Card><CardContent className="p-5" data-testid="stat-total-earned">
          <div className="text-xs font-mono uppercase text-slate-500">Total earned</div>
          <div className="font-display font-extrabold text-3xl text-slate-900 mt-2">${(earnings?.total_earned || 0).toFixed(2)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5" data-testid="stat-total-withdrawn">
          <div className="text-xs font-mono uppercase text-slate-500">Total withdrawn</div>
          <div className="font-display font-extrabold text-3xl text-slate-900 mt-2">${(earnings?.total_withdrawn || 0).toFixed(2)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5" data-testid="stat-in-flight">
          <div className="text-xs font-mono uppercase text-slate-500">In-flight withdrawals</div>
          <div className="font-display font-extrabold text-3xl text-slate-900 mt-2">${(earnings?.in_flight_withdrawals || 0).toFixed(2)}</div>
        </CardContent></Card>
      </div>

      <div>
        <h2 className="font-display font-bold text-lg text-slate-900 mb-3">Payout history</h2>
        {withdrawals.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-slate-500" data-testid="withdrawals-empty">
            No withdrawals yet. Once your balance grows, use the Withdraw button above.
          </CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="withdrawals-list">
            {withdrawals.map((w) => (
              <Card key={w.id || w.stripe_transfer_id} className="border-slate-200" data-testid={`withdrawal-${w.id || w.stripe_transfer_id}`}>
                <CardContent className="p-4 grid md:grid-cols-4 gap-3 items-center">
                  <div>
                    <div className="font-display font-bold text-lg text-slate-900">${(w.amount || 0).toFixed(2)}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{w.stripe_transfer_id || w.id || ""}</div>
                  </div>
                  <div className="text-xs text-slate-600 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {w.created_at ? new Date(w.created_at).toLocaleString() : "—"}
                  </div>
                  <div className="text-xs text-slate-600">
                    {w.destination_last4 ? <>To ••••{w.destination_last4}</> : (w.method || "Bank transfer")}
                  </div>
                  <div className="md:text-right">
                    <Badge className={`${WD_BADGE[w.status] || "bg-slate-100 text-slate-700"} border font-mono text-[10px] uppercase`}>
                      {(w.status || "unknown").replace(/_/g, " ")}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={wdOpen} onOpenChange={setWdOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Withdraw earnings</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-xl bg-teal-50 border border-teal-200">
              <div className="text-xs font-mono uppercase tracking-widest text-teal-700">Available</div>
              <div className="font-display font-extrabold text-3xl text-slate-900">${earnings?.available_balance.toFixed(2)}</div>
              <div className="text-xs text-slate-500 mt-1">Payout to {earnings?.stripe_status?.external_bank_name || "your bank"}{earnings?.stripe_status?.external_last4 ? ` ••••${earnings.stripe_status.external_last4}` : ""}</div>
            </div>
            <div>
              <Label>Amount (USD)</Label>
              <Input type="number" min="0.01" step="0.01" value={wdAmount} onChange={(e) => setWdAmount(e.target.value)} data-testid="withdraw-amount" className="mt-1.5 font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWdOpen(false)}>Cancel</Button>
            <Button onClick={doWithdraw} data-testid="withdraw-confirm" className="bg-teal-600 text-white">Confirm withdraw</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
